import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { EMAIL, SMS, shortChannelLabel, toggleChannel, type BroadcastChannel } from '../../../lib/broadcastChannels';
import { formatInZone, resolveTripTimeZone } from '../../../lib/tripTime';
import {
  audienceKindPhrase,
  buildPlanDrafts,
  buildPlanRequests,
  classifyPlanError,
  defaultPlanChannels,
  describePlanError,
  newSequenceId,
  planIssues,
  planPrerequisite,
  setStepWhen,
  stepDisplayName,
  type PlanRequest,
  type PlanStepDraft,
} from '../../../lib/reminderPlan';
import { unresolvedPlan } from '../../../lib/reminderPlanGuard';
import { planErrorsByStep } from '../../../lib/reminderPlanErrors';
import { planStepSmsMessage, smsLimitIssues } from '../../../lib/reminderPlanSms';
import { plural } from '../../../lib/plural';
import PlanErrorText from './PlanErrorText';
import PlanStepEditor from './PlanStepEditor';
import { useAudienceKindSupport } from './useAudienceKindSupport';
import { usePlanCreateBlock } from './usePlanCreateBlock';
import { useReminderPlanActions } from './useReminderPlanActions';
import { useSmsEstimates } from './useSmsEstimates';
import type { GroupDetail } from '../../../types/groups';

type Stage = 'edit' | 'confirm' | 'done';

const UNAVAILABLE = "Reminder plans aren't available yet. You can still schedule a single send with “Send at…”.";

/**
 * "Set up reminders": four preset steps (RSVP chase, week out, day before, morning of), each toggleable, dated in
 * the TRIP zone, with editable copy — then a confirm that lists every send (when, who, channel) before anything is
 * created.
 *
 * The plan is created by ONE atomic call under a client-minted `sequenceId`: the API writes every step or none, so
 * there is no half-made plan to finish, cancel or rebuild. A failure that is a definite answer (a step the API
 * rejects, named by index) writes nothing and is simply shown. A timeout or 5xx is an UNKNOWN outcome: the attempt
 * (its id and steps) is remembered, the list is re-read, and the SAME call is retried — the API answers it from what
 * exists — so it can never make a second plan; Create stays off (Retry / Discard instead) until it resolves. Several
 * plans may coexist (owner decision 2026-09-29); nothing about other plans blocks Create. The dialog ignores
 * Escape/backdrop while a create is running.
 *
 * Needs a real trip zone and a start date, and a server that decides RSVP audiences at send time (#394). Without
 * them it explains why, and never builds a plan that would fire at the wrong hour or reach the wrong guests. Plain
 * single-send scheduling (ScheduleControls) is untouched by any of it.
 */
export default function ReminderPlanDialog({
  group,
  onClose,
  onOpenOverview,
}: {
  group: GroupDetail;
  onClose: () => void;
  onOpenOverview?: () => void;
}) {
  const { timeZone: resolvedZone, isFallback } = resolveTripTimeZone(group.timeZone);
  const prereq = planPrerequisite(group.dateStart, group.timeZone, isFallback);
  const { supported, checked } = useAudienceKindSupport(group.id);
  const { createPlan, discardAttempt } = useReminderPlanActions(group.id);
  const block = usePlanCreateBlock(group.id);

  const [stage, setStage] = useState<Stage>('edit');
  const [drafts, setDrafts] = useState<PlanStepDraft[]>(() => (
    prereq.ok ? buildPlanDrafts({ title: group.title, dateStart: prereq.dateStart, timeZone: prereq.timeZone }) : []
  ));
  const [channels, setChannels] = useState<BroadcastChannel[]>(() => defaultPlanChannels(group.invitees));
  const [createdCount, setCreatedCount] = useState(0);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The same refusal, split by step, so each step's own editor can show what is wrong with IT after Back.
  const [stepErrors, setStepErrors] = useState<Record<string, string[]>>({});
  const [note, setNote] = useState<string | null>(null);
  // Discarding on an API that cannot be asked to refuse a late commit needs the host's confirmation.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Synchronous guard: a double click lands before `isLoading` re-renders.
  const creating = useRef(false);

  const smsOn = channels.includes(SMS);
  const personalize = group.accessMode === 'invite_only';
  // The server counts each text as it will be sent (heading, link and all); a labelled local count stands in meanwhile.
  const { estimateFor } = useSmsEstimates({
    groupId: group.id,
    items: smsOn ? drafts.filter((draft) => draft.enabled).map((draft) => ({ id: draft.key, message: planStepSmsMessage(draft) })) : [],
    personalize,
    trip: { title: group.title, slug: group.slug, location: group.locationName },
  });
  const issues = prereq.ok
    ? [...planIssues(drafts, channels, prereq.timeZone), ...smsLimitIssues(drafts, channels, estimateFor)]
    : [];
  const unavailable = serverUnavailable || (checked && !supported);
  const patch = (index: number, change: Partial<PlanStepDraft>) => (
    setDrafts((prev) => prev.map((draft, i) => (i === index ? { ...draft, ...change } : draft)))
  );

  // What the confirm lists. Built once per edit (the display id is throwaway; the plan's real id is minted at the click).
  const requests = useMemo(() => (
    prereq.ok && stage !== 'edit'
      ? buildPlanRequests({
        drafts,
        channels,
        timeZone: prereq.timeZone,
        dateStart: prereq.dateStart,
        personalizeInviteLinks: personalize,
        sequenceId: newSequenceId(),
      })
      : []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [stage, drafts, channels, personalize, prereq.ok ? prereq.timeZone : '', prereq.ok ? prereq.dateStart : '']);

  /** Send one plan call — a NEW plan (fresh id) or the SAME unresolved one — and report what it did. */
  const send = async (sequenceId: string, anchorDate: string, planRequests: PlanRequest[]) => {
    if (creating.current) return;
    creating.current = true;
    setError(null);
    setStepErrors({});
    setNote(null);
    setConfirmDiscard(false);
    try {
      const result = await createPlan.mutateAsync({ sequenceId, anchorDate, requests: planRequests });
      setCreatedCount(result.rows.length);
      setStage('done');
    } catch (err) {
      const kind = classifyPlanError(err);
      if (kind === 'unavailable' && !unresolvedPlan(group.id, sequenceId)) {
        setServerUnavailable(true);
        setStage('edit');
      } else if (kind === 'unavailable') {
        // "Not available" from a RETRY after an unknown outcome (the migration latch) proves nothing about the earlier request: the attempt
        // stays open, and the panel beside Retry / Discard explains why instead of switching the dialog to "not available".
        setError(null);
      } else {
        const keys = planRequests.map((entry) => entry.step);
        setError(describePlanError(err, keys));
        setStepErrors(planErrorsByStep(err, keys, stepDisplayName));
      }
    } finally {
      creating.current = false;
    }
  };

  const create = () => {
    if (!prereq.ok || block.blocked) return;
    // A fresh id per NEW plan, minted at the click. A plan whose outcome is unknown is never re-created under a
    // new id: Create is off while one is unresolved, and it is retried through `retry` (same id, same steps).
    const fresh = buildPlanRequests({
      drafts,
      channels,
      timeZone: prereq.timeZone,
      dateStart: prereq.dateStart,
      personalizeInviteLinks: personalize,
      sequenceId: newSequenceId(),
    });
    return send(fresh[0]?.request.sequenceId ?? newSequenceId(), prereq.dateStart, fresh);
  };

  /** Retry the SAME plan (same id, same steps): the API answers a repeat from what exists. */
  const retry = () => {
    if (!block.unresolved) return;
    return send(block.unresolved.sequenceId, block.unresolved.anchorDate, block.unresolved.requests);
  };

  const discard = async (confirmedRisk = false) => {
    if (!block.unresolved) return;
    const result = await discardAttempt(block.unresolved, { confirmedRisk });
    setConfirmDiscard(Boolean(result.needsConfirm));
    setNote(result.note);
  };

  const requestClose = () => {
    // Escape or a backdrop click must not orphan a create that is still running.
    if (createPlan.isLoading || creating.current) return;
    onClose();
  };

  const alerts = stage === 'done' || unavailable ? null : (
    <Stack spacing={1}>
      {note ? (
        <Alert
          severity={confirmDiscard ? 'warning' : 'info'}
          onClose={() => { setNote(null); setConfirmDiscard(false); }}
          action={confirmDiscard ? <Button color="inherit" size="small" onClick={() => discard(true)}>Discard anyway</Button> : undefined}
        >
          {note}
        </Alert>
      ) : null}
      {block.writing ? (
        <Alert severity="info" data-testid="plan-writing">
          A plan is being created right now — Create is off until it finishes, so the same plan can&apos;t be made twice.
        </Alert>
      ) : null}
      {error ? <Alert severity="error" data-testid="plan-error"><PlanErrorText text={error} /></Alert> : null}
      {block.unresolved ? (
        <Alert
          severity="warning"
          data-testid="plan-unresolved"
          action={(
            <Stack direction="row" spacing={0.5}>
              <Button color="inherit" size="small" disabled={createPlan.isLoading} onClick={retry}>Retry the same plan</Button>
              <Button color="inherit" size="small" disabled={createPlan.isLoading} onClick={() => discard(false)}>Discard it</Button>
            </Stack>
          )}
        >
          <Typography variant="body2" fontWeight={700}>
            An earlier attempt to create a plan didn&apos;t finish, and we couldn&apos;t confirm whether it was saved.
          </Typography>
          {block.unresolved.note ? (
            <Typography variant="body2" data-testid="plan-unresolved-note" sx={{ mt: 0.5 }}>{block.unresolved.note}</Typography>
          ) : null}
          <Typography variant="body2">
            Retrying sends the very same plan again — nothing is duplicated if it was saved. Discarding asks the server to
            refuse it should the earlier request still be on its way. A retry that succeeds, a &quot;this plan already exists&quot;
            answer, or the plan showing up on the list also resolves it; a refusal of a retry does not. Create is
            off until then, so a plan that may already exist can&apos;t be made twice.
          </Typography>
        </Alert>
      ) : null}
    </Stack>
  );

  return (
    <Dialog open onClose={requestClose} fullWidth maxWidth="md">
      <DialogTitle>Set up reminders</DialogTitle>
      <DialogContent dividers>
        {!prereq.ok ? (
          <Stack spacing={1.5}>
            <Alert severity="info">{prereq.reason}</Alert>
            {onOpenOverview ? (
              <Box>
                <Button variant="outlined" onClick={() => { onClose(); onOpenOverview(); }}>Open the Overview tab</Button>
              </Box>
            ) : null}
          </Stack>
        ) : unavailable ? (
          <Alert severity="info">{UNAVAILABLE}</Alert>
        ) : stage === 'done' ? (
          <Alert severity="success">
            {plural(createdCount, 'reminder')} scheduled. They are in the Scheduled sends list below, where the whole
            plan can be cancelled in one click.
          </Alert>
        ) : stage === 'confirm' ? (
          <Stack spacing={1.5}>
            <Typography>
              Create {requests.length === 1 ? 'this reminder' : `these ${requests.length} reminders`}? Each one works out who gets it when it sends, so a guest who
              replies in the meantime is left alone.
            </Typography>
            {requests.map((step) => (
              <Box key={step.sendAt + step.label} data-testid="plan-confirm-row">
                <Typography variant="subtitle2" fontWeight={700}>{step.request.subject}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatInZone(step.sendAt, prereq.timeZone)} · {audienceKindPhrase(step.audienceKind)} ·{' '}
                  {(step.request.channels ?? []).map(shortChannelLabel).join(' + ')}
                </Typography>
              </Box>
            ))}
            {alerts}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Times are local to the trip ({prereq.timeZone}). Every reminder needs different wording — guests are only
              sent identical text once a day — so each one names what it is for and the trip date.
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={shortChannelLabel(EMAIL)}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={smsOn}
                    onChange={(e) => setChannels((prev) => toggleChannel(prev, SMS, e.target.checked))}
                  />
                )}
                label={shortChannelLabel(SMS)}
              />
            </Stack>
            {drafts.map((draft, index) => (
              <PlanStepEditor
                key={draft.key}
                draft={draft}
                timeZone={resolvedZone}
                smsOn={smsOn}
                smsEstimate={estimateFor(draft.key)}
                errors={stepErrors[draft.key]}
                onChange={(change) => patch(index, change)}
                onWhen={(when) => setDrafts((prev) => prev.map((d, i) => (
                  i === index ? setStepWhen(d, when, resolvedZone) : d
                )))}
              />
            ))}
            {alerts}
            {issues.length > 0 ? (
              <Alert severity="warning">
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {issues.map((issue) => <li key={issue}>{issue}</li>)}
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {stage === 'done' || !prereq.ok || unavailable ? (
          <Button onClick={onClose}>Close</Button>
        ) : stage === 'confirm' ? (
          <>
            <Button onClick={() => setStage('edit')} disabled={createPlan.isLoading}>Back</Button>
            <Button onClick={onClose} disabled={createPlan.isLoading}>Close</Button>
            <Button
              variant="contained"
              disabled={createPlan.isLoading || requests.length === 0 || block.blocked}
              onClick={create}
            >
              {createPlan.isLoading ? 'Creating…' : `Create ${plural(requests.length, 'reminder')}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {/* Never before the server has PROVED it decides audiences at send
                time: an older API ignores audienceKind and would schedule
                audience-less rows, i.e. the whole roster. */}
            <Button
              variant="contained"
              disabled={issues.length > 0 || !supported}
              onClick={() => { setError(null); setStage('confirm'); }}
            >
              {checked ? 'Review' : 'Checking the server…'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
